import { type Except, notEmpty, type Optional } from '@festina-life/core'
import { useCallback, useMemo } from 'react'
import {
  NumericFormat,
  numericFormatter,
  type OnValueChange,
} from 'react-number-format'

import { createNumberInputKeyDownHandler } from '../numberInput/createNumberInputKeyDownHandler'
import { useNumberFormatOptions } from '../numberInput/useNumberFormatOptions'
import { RawTextInput, type RawTextInputProps } from '../textInput/RawTextInput'
import {
  convertPercentage,
  deconvertPercentage,
  DEFAULT_PERCENT_DECIMAL_SCALE,
  PERCENT_SUFFIX,
} from './percentageInput.utils'

export { DEFAULT_PERCENT_DECIMAL_SCALE } from './percentageInput.utils'

export type RawPercentageInputProps = Except<
  RawTextInputProps,
  'value' | 'onChange' | 'type' | 'defaultValue' | 'placeholder'
> & {
  value?: Optional<number>
  name?: string
  defaultValue?: Optional<string | number>
  placeholder?: Optional<string | number>
  onChange?: (value: number | undefined) => void
  decimalScale?: number
}

export const RawPercentageInput = ({
  value,
  onChange,
  placeholder,
  decimalScale = DEFAULT_PERCENT_DECIMAL_SCALE,
  onKeyDown,
  ...restProps
}: RawPercentageInputProps) => {
  const numberFormatOptions = useNumberFormatOptions(decimalScale)
  const handleKeyDown = createNumberInputKeyDownHandler(
    { ...numberFormatOptions, suffix: PERCENT_SUFFIX },
    onKeyDown,
  )

  const convertedInternalValue = useMemo(
    () => (notEmpty(value) ? convertPercentage(value, decimalScale) : null),
    [decimalScale, value],
  )

  const formattedPlaceholder = useMemo(() => {
    let parsedPlaceholder = placeholder

    if (typeof placeholder === 'string') {
      parsedPlaceholder = isNaN(parseFloat(placeholder))
        ? placeholder
        : convertPercentage(placeholder, decimalScale)
    } else if (typeof placeholder === 'number') {
      parsedPlaceholder = convertPercentage(placeholder, decimalScale)
    }

    return `${numericFormatter(
      parsedPlaceholder?.toString() ?? '',
      numberFormatOptions,
    )}${typeof parsedPlaceholder === 'number' ? PERCENT_SUFFIX : ''}`
  }, [decimalScale, numberFormatOptions, placeholder])

  const handleChange: OnValueChange = useCallback(
    ({ value }) => {
      // Manually convert visible percentage to internal percentage value

      const convertedNumber = value
        ? deconvertPercentage(value, decimalScale)
        : undefined

      onChange?.(convertedNumber)
    },
    [decimalScale, onChange],
  )

  return (
    <NumericFormat
      value={convertedInternalValue}
      suffix={PERCENT_SUFFIX}
      placeholder={formattedPlaceholder}
      onValueChange={handleChange}
      customInput={RawTextInput}
      onKeyDown={handleKeyDown}
      {...restProps}
      {...numberFormatOptions}
    />
  )
}
